import { useCallback, useMemo } from 'react';

import { NavigationIntents } from '../../types/onboarding';

interface CardFilterConfig {
  individual: {
    include: Array<keyof NavigationIntents>;
    exclude: Array<keyof NavigationIntents>;
  };
  combined: {
    include: Array<keyof NavigationIntents>;
    exclude: Array<keyof NavigationIntents>;
  };
  singleCause: {
    include: Array<keyof NavigationIntents>;
    exclude: Array<keyof NavigationIntents>;
  };
}

const CARD_FILTER_CONFIG: CardFilterConfig = {
  individual: {
    include: ['visitCause', 'visitFees'],
    exclude: ['visitRewards', 'visitAccount', 'visitPurchases', 'visitRewardsActivity'],
  },
  combined: {
    include: ['visitRewards', 'visitAccount', 'visitPurchases', 'visitRewardsActivity'],
    exclude: ['visitCause', 'visitFees'],
  },
  singleCause: {
    include: [
      'visitAccount',
      'visitCause',
      'visitFees',
      'visitRewards',
      'visitPurchases',
      'visitRewardsActivity',
    ],
    exclude: [],
  },
};

interface UseCardFilteringReturn {
  individualCardIntents: NavigationIntents;
  combinedCardIntents: NavigationIntents | false;
}

/**
 * Filters intents based on card type and multiple cause logic
 */
export const useCardFiltering = (
  navigationIntents: NavigationIntents,
  hasMoreThanOneCause: boolean
): UseCardFilteringReturn => {
  const applyFilter = useCallback(
    (intents: NavigationIntents, filterType: keyof CardFilterConfig): NavigationIntents => {
      const config = CARD_FILTER_CONFIG[filterType];
      const filtered = { ...intents };

      config.exclude.forEach((key) => {
        filtered[key] = false;
      });

      if (config.include.length > 0) {
        Object.keys(filtered).forEach((key) => {
          const intentKey = key as keyof NavigationIntents;
          if (!config.include.includes(intentKey)) {
            filtered[intentKey] = false;
          }
        });
      }

      return filtered;
    },
    []
  );

  /**
   * Individual card intents with explicit rule application
   */
  const individualCardIntents = useMemo(() => {
    const filterType = hasMoreThanOneCause ? 'individual' : 'singleCause';
    return applyFilter(navigationIntents, filterType);
  }, [navigationIntents, hasMoreThanOneCause, applyFilter]);

  /**
   * Combined card intents - only exists for multiple causes
   */
  const combinedCardIntents = useMemo(() => {
    if (!hasMoreThanOneCause) {
      return false;
    }
    return applyFilter(navigationIntents, 'combined');
  }, [navigationIntents, hasMoreThanOneCause, applyFilter]);

  return {
    individualCardIntents,
    combinedCardIntents,
  };
};
